import { useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { telemetry } from '../lib/telemetry'
import { useAuthStore } from '../stores/authStore'

/**
 * Monta o listener `onAuthStateChange` UMA UNICA VEZ no topo da arvore.
 * Nao renderiza nada visivel. Atualiza o authStore conforme eventos chegam.
 *
 * Estrategia anti-travamento:
 * - Confia no evento INITIAL_SESSION do listener (dispara em <1s no boot).
 * - Watchdog de 10s no boot: se o listener nao disparar, libera loading.
 *
 * Estrategia anti-flicker do "Acesso nao configurado":
 * - Eventos TOKEN_REFRESHED (a cada ~1h) reentregavam a mesma session,
 *   disparando fetchProfile() de novo. Se essa query lampeja, profile vira
 *   null e a UI mostra UnconfiguredAccess ate o user dar F5.
 * - Agora so re-fetch o profile quando o user.id muda OU quando profile
 *   ainda nao foi carregado. Token refresh nao re-fetch.
 */
export function AuthInit() {
  useEffect(() => {
    let lastUserId = null

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      telemetry.breadcrumb('auth', event, { hasSession: !!session })
      const { setSession, fetchProfile, setProfile, setLoading, profile } = useAuthStore.getState()
      setSession(session)

      if (!session) {
        lastUserId = null
        setProfile(null)
        setLoading(false)
        return
      }

      const userChanged = session.user?.id !== lastUserId
      lastUserId = session.user?.id ?? null

      // Re-fetch profile so quando user trocou ou ainda nao temos profile.
      // Em TOKEN_REFRESHED do mesmo user (caso comum), nao mexe no profile
      // ja carregado pra evitar flicker de "Acesso nao configurado".
      // Fora do callback: query do supabase feita DENTRO do onAuthStateChange
      // espera o lock interno do auth, que o proprio callback segura. Na
      // abertura direta de um link (ex.: o do e-mail) isso travava ate o
      // timeout e a tela caia em "Acesso nao configurado".
      if (userChanged || !profile) {
        setTimeout(() => {
          fetchProfile()
        }, 0)
      } else {
        setLoading(false)
      }
    })

    const watchdog = setTimeout(() => {
      const { loading, setLoading } = useAuthStore.getState()
      if (loading) {
        telemetry.captureMessage('auth.watchdog.released', { reason: 'no-session-event-in-10s' })
        setLoading(false)
      }
    }, 10000)

    return () => {
      subscription.unsubscribe()
      clearTimeout(watchdog)
    }
  }, [])

  return null
}
